import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import OrganField from "./components/organ-field";
import ServicesField from "./components/services-field";
import PrioritiesField from "./components/priorities-field";
import AttendanceLocalField from "./components/attendance-local-field";
import ColorPickerInput from "../../../Components/ColorPickerInput";
import LogoField from "./components/logo-field";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import { Totem } from "../../../@types/totem";
import { TotemCreateData } from "../../../services/api-tansquery/totem/create";
import { TotemUpdateData } from "../../../services/api-tansquery/totem/update";
import { useTotemGenerateToken } from "../../../services/api-tansquery/totem/generate_token";
import { TotemDuplicateData } from "../../../services/api-tansquery/totem/duplicate";

type FormProps = {
    action: "create" | "update" | "duplicate";
    totem?: Totem;
    mutation?: UseMutationResult<
        Totem,
        unknown,
        | TotemCreateData
        | TotemUpdateData
        | TotemDuplicateData,
        unknown
    >
};
const initialValues: z.infer<typeof schema> = {
    ativo: true,
    token: "",
    descricao: "",
    local_atendimento: "",
    orgao: "",
    servicos: [],
    prioridades: [],
    senha_reset: "",
    cor_fonte: "",
    cor_fundo: "",
    logo: null,
}

export default function Form({ action, totem, mutation }: FormProps) {
    const history = useHistory();
    const { classes } = useFormStyles();
    const { data: generatedToken } = useTotemGenerateToken({
        enabled: ['create', 'duplicate'].includes(action)
    });
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: initialValues
    });
    const watchedOrgao = form.watch("orgao");
    const watchedLocalAtendimento = form.watch("local_atendimento");

    useEffect(() => {
        let values = initialValues;

        if (totem) {
            values = {
                ativo: totem.ativo,
                token: totem.token || "",
                senha_reset: totem.senha_reset || "",
                descricao: `${totem.descricao}${action === 'duplicate' ? " - Cópia" : ""}` || "",
                local_atendimento: totem.local_atendimento?.slug || "",
                orgao: totem.orgao.id.toString() || "",
                servicos: totem.servicos.map(servico => servico.id.toString()) || [],
                prioridades: totem.prioridades.map(prioridade => prioridade.id.toString()) || [],
                cor_fonte: totem.cor_fonte.toUpperCase(),
                cor_fundo: totem.cor_fundo.toUpperCase(),
                logo: totem.logo || null,
            };
        }

        if (generatedToken) {
            values.token = generatedToken.token;
            values.senha_reset = generatedToken.senha_reset;
        }

        form.reset(values);
    }, [form, totem, generatedToken, action]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        const payload = {
            id: totem?.id,
            ativo: data.ativo,
            descricao: data.descricao,
            local_atendimento: data.local_atendimento,
            orgao: Number(data.orgao),
            servicos: data.servicos,
            prioridades: data.prioridades,
            cor_fonte: data.cor_fonte,
            cor_fundo: data.cor_fundo,
            logo: data.logo,
            token: data.token,
            senha_reset: data.senha_reset,
        }

        if (action === "create") {
            delete payload.id;
        }

        await mutation.mutateAsync(payload);
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    useEffect(() => {
        if (form.formState.isDirty) {
            form.setValue("local_atendimento", "");
        }
    }, [watchedOrgao, form]);

    useEffect(() => {
        if (form.formState.isDirty) {
            form.setValue("servicos", []);
        }
    }, [watchedLocalAtendimento, form]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Typography className={classes.title}>
                Gerenciar Totem
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Controller
                        name="token"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Token"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Informe o token do totem"}
                                InputProps={{ readOnly: true, required: false }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="descricao"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Descrição"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Descreva o propósito ou localização do totem"}
                                InputProps={{
                                    required: false,
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="ativo"
                        control={form.control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={<Switch {...field} checked={field.value} />}
                                label="Este totem está ativo?"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="orgao"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <OrganField
                                totem={totem}
                                field={field}
                                fieldState={fieldState}
                                restrictToCurrentOrgao={action === "create"}
                            />
                        )}
                    />
                </Grid>
                {form.watch("orgao") && (
                    <Grid item xs={12}>
                        <Controller
                            name="local_atendimento"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <AttendanceLocalField
                                    totem={totem}
                                    field={field}
                                    fieldState={fieldState}
                                    orgao={form.watch("orgao")}
                                />
                            )}
                        />
                    </Grid>
                )}
                {form.watch("local_atendimento") && (
                    <Grid item xs={12}>
                        <Controller
                            name="servicos"
                            control={form.control}
                            render={({ field, fieldState }) => (
                                <ServicesField
                                    totem={totem}
                                    field={field}
                                    fieldState={fieldState}
                                    unidade_slug={form.watch("local_atendimento")}
                                />
                            )}
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Controller
                        name="prioridades"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <PrioritiesField
                                field={field}
                                fieldState={fieldState}
                                priorities={totem?.prioridades}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Controller
                        name="senha_reset"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                required
                                label="Senha de Reset"
                                variant="outlined"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message || "Informe a senha para resetar o totem"}
                                InputProps={{ readOnly: true, required: false }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Controller
                        name="cor_fonte"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ColorPickerInput
                                {...field}
                                required
                                fullWidth
                                label="Cor da Fonte"
                                variant="outlined"
                                error={!!fieldState.error}
                                value={field.value}
                                onChange={e => field.onChange(e.target.value?.toUpperCase())}
                                helperText={fieldState.error?.message || "Exemplo: #000000"}
                                InputProps={{ required: false }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Controller
                        name="cor_fundo"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ColorPickerInput
                                {...field}
                                required
                                fullWidth
                                label="Cor de Fundo"
                                variant="outlined"
                                error={!!fieldState.error}
                                value={field.value}
                                onChange={e => field.onChange(e.target.value?.toUpperCase())}
                                helperText={fieldState.error?.message || "Exemplo: #FFFFFF"}
                                InputProps={{ required: false }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="logo"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <LogoField
                                label="Logo do Totem"
                                field={field}
                                fieldState={fieldState}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
